// src/components/GridView/hooks/useHiddenColumns.ts
import { useState, useEffect, useCallback } from 'react';
import { gridViewService } from '../service';
import type { GridColumn } from '../types';

// ==================== Key Helper ====================

/**
 * Returns the API key for a column: "{layerId}_{customFieldId}".
 * Fixed columns (title, created-at) have undefined ids → "undefined_undefined",
 * which never matches any real hidden key.
 */
export function getColumnApiKey(col: Pick<GridColumn, 'layerId' | 'customFieldId'>): string
{
  return `${col.layerId}_${col.customFieldId}`;
}

// ==================== Hook ====================

interface UseHiddenColumnsOptions {
  dashboardId: number;
  configValueId?: number;
  projectId?: string;
}

interface UseHiddenColumnsReturn {
  hiddenKeys: Set<string>;
  hideColumn: (key: string) => Promise<void>;
  showColumn: (key: string) => Promise<void>;
  applyHiddenColumns: (keys: string[]) => Promise<void>;
}

export function useHiddenColumns({
  dashboardId,
  configValueId,
  projectId,
}: UseHiddenColumnsOptions): UseHiddenColumnsReturn
{
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

  // Load initial hidden columns from API on mount and when context changes
  useEffect(() =>
  {
    let cancelled = false;
    (async () =>
    {
      const keys = await gridViewService.getHiddenColumns({
        dashboardId,
        configValueId,
        projectId: projectId ? parseInt(projectId, 10) : 0,
      });
      if (!cancelled)
      {
        setHiddenKeys(new Set(keys));
      }
    })();
    return () =>
    {
      cancelled = true;
    };
  }, [dashboardId, configValueId, projectId]);

  // Persist a new set of keys to the API
  const saveKeys = useCallback(
    async (newKeys: Set<string>) =>
    {
      await gridViewService.updateHiddenColumns({
        DashboardId: dashboardId,
        Project: projectId ? parseInt(projectId, 10) : 0,
        ConfigValueId: configValueId,
        Columns: Array.from(newKeys).join(','),
      });
    },
    [dashboardId, configValueId, projectId],
  );

  const hideColumn = useCallback(
    async (key: string) =>
    {
      setHiddenKeys(prev =>
      {
        const next = new Set(prev);
        next.add(key);
        saveKeys(next);
        return next;
      });
    },
    [saveKeys],
  );

  const showColumn = useCallback(
    async (key: string) =>
    {
      setHiddenKeys(prev =>
      {
        const next = new Set(prev);
        next.delete(key);
        saveKeys(next);
        return next;
      });
    },
    [saveKeys],
  );

  const applyHiddenColumns = useCallback(
    async (keys: string[]) =>
    {
      const newKeys = new Set(keys);
      setHiddenKeys(newKeys);
      await saveKeys(newKeys);
    },
    [saveKeys],
  );

  return { hiddenKeys, hideColumn, showColumn, applyHiddenColumns };
}
